/**
 * API Client Configuration
 * Centralized Axios instance with interceptors.
 *
 * Auth transport: httpOnly cookies (set by backend).
 * The browser sends cookies automatically when withCredentials is true.
 * No localStorage token storage. No manual Authorization header.
 */

import axios from 'axios';
import { normalizeApiError } from '../utils/errorNormalizer';
import { clearHintCookie } from './authService';
import { isDemoMode } from './demoMode';
import { resolveDemoRequest } from '../demo/demoRoutes';
import { notifyDemoWrite, notifySideEffect } from '../demo/demoNudge';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true, // Send httpOnly cookies on every request
  headers: {
    'Content-Type': 'application/json',
  },
});

// Demo mode: resolve requests against in-memory fixtures instead of the network.
// Installed once; the real adapter is used whenever demo mode is off.
const realAdapter = axios.getAdapter(apiClient.defaults.adapter);
apiClient.defaults.adapter = (config) =>
  (isDemoMode() ? resolveDemoRequest(config) : realAdapter(config));

// Request interceptor — no auth header needed (cookies are automatic)
apiClient.interceptors.request.use(
  (config) => config,
  (error) => Promise.reject(error),
);

// Response interceptor - handle errors & token refresh
apiClient.interceptors.response.use(
  (response) => {
    if (isDemoMode()) {
      const method = (response.config?.method || 'get').toLowerCase();
      if (response.data?.demoNudge) notifySideEffect(response.data.action);
      else if (method !== 'get') notifyDemoWrite();
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 - try to refresh token via cookie
    // Skip refresh for auth endpoints that expect 401 as a normal response
    const url = originalRequest?.url || '';
    const isAuthEndpoint = url.includes('/auth/2fa/challenge') || url.includes('/auth/login');
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      try {
        // Refresh token is in httpOnly cookie — just POST with empty body.
        // The cookie is sent automatically because of withCredentials.
        await axios.post(`${API_BASE_URL}/auth/refresh`, {}, {
          withCredentials: true,
        });

        // Retry original request — new access cookie is now set
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed — clear the JS hint cookie and redirect to login.
        // The httpOnly cookies are already expired or invalid; clearing the
        // hint prevents an infinite redirect loop (isAuthenticated → true →
        // /auth/me → 401 → refresh → fail → redirect → isAuthenticated…).
        clearHintCookie();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Handle 402 — broadcast billing events so UI can show upgrade modal
    if (error.response?.status === 402) {
      const code = error.response.data?.detail?.code;
      if (code === 'TRIAL_EXPIRED') {
        window.dispatchEvent(
          new CustomEvent('adflow:trial-expired', { detail: error.response.data.detail })
        );
      } else if (code === 'SUBSCRIPTION_REQUIRED') {
        window.dispatchEvent(
          new CustomEvent('adflow:subscription-required', { detail: error.response.data.detail })
        );
      }
    }

    // Handle 503 — maintenance mode: redirect to /maintenance page
    if (error.response?.status === 503 && error.response?.data?.status === 'maintenance') {
      const message = error.response.data.message || '';
      const params = message ? `?message=${encodeURIComponent(message)}` : '';
      if (window.location.pathname !== '/maintenance') {
        window.location.href = `/maintenance${params}`;
      }
      return Promise.reject(error);
    }

    // Handle 403 — feature/limit gates trigger upgrade prompt
    if (error.response?.status === 403) {
      const code = error.response.data?.detail?.code;
      if (code === 'EMAIL_NOT_VERIFIED') {
        window.dispatchEvent(new CustomEvent('adflow:email-not-verified'));
      }
      const gatedCodes = [
        'FEATURE_NOT_AVAILABLE', 'ACCOUNT_LIMIT_REACHED',
        'CAMPAIGN_LIMIT_REACHED', 'RULE_LIMIT_REACHED',
      ];
      if (gatedCodes.includes(code)) {
        window.dispatchEvent(
          new CustomEvent('adflow:upgrade-required', { detail: error.response.data.detail })
        );
      }
    }

    // Normalize to a consistent error shape understood by all consumers
    return Promise.reject(normalizeApiError(error));
  }
);

export default apiClient;

// Helper functions for common HTTP methods
export const api = {
  get: (url, config = {}) => apiClient.get(url, config),
  post: (url, data, config = {}) => apiClient.post(url, data, config),
  put: (url, data, config = {}) => apiClient.put(url, data, config),
  patch: (url, data, config = {}) => apiClient.patch(url, data, config),
  delete: (url, config = {}) => apiClient.delete(url, config),
};
